import { motion } from 'framer-motion'
import {
  AlertCircle,
  TrendingUp,
  Calendar,
  Target,
  Sparkles,
  CheckCircle,
  Euro,
  Clock,
} from 'lucide-react'
import FeatureLayout from '../../components/FeatureLayout'
import { useLanguage } from '../../contexts/LanguageContext'

export default function DispoPreventionPage() {
  const { t } = useLanguage()

  const steps = [
    {
      icon: TrendingUp,
      title: t('dispo.modal.step1.title'),
      description: t('dispo.modal.step1.description'),
      color: 'red',
    },
    {
      icon: AlertCircle,
      title: t('dispo.modal.step2.title'),
      description: t('dispo.modal.step2.description'),
      color: 'orange',
    },
    {
      icon: Calendar,
      title: t('dispo.modal.step3.title'),
      description: t('dispo.modal.step3.description'),
      color: 'yellow',
    },
    {
      icon: Target,
      title: t('dispo.modal.step4.title'),
      description: t('dispo.modal.step4.description'),
      color: 'green',
    },
  ]

  const costReasons = [
    {
      icon: Euro,
      title: t('dispo.modal.cost1.title'),
      description: t('dispo.modal.cost1.description'),
    },
    {
      icon: Clock,
      title: t('dispo.modal.cost2.title'),
      description: t('dispo.modal.cost2.description'),
    },
    {
      icon: AlertCircle,
      title: t('dispo.modal.cost3.title'),
      description: t('dispo.modal.cost3.description'),
    },
  ]

  const aiFeatures = [
    {
      icon: TrendingUp,
      title: t('dispo.modal.feature1.title'),
      description: t('dispo.modal.feature1.description'),
    },
    {
      icon: AlertCircle,
      title: t('dispo.modal.feature2.title'),
      description: t('dispo.modal.feature2.description'),
    },
    {
      icon: Target,
      title: t('dispo.modal.feature3.title'),
      description: t('dispo.modal.feature3.description'),
    },
  ]

  return (
    <FeatureLayout
      title={t('dispo.modal.title')}
      subtitle={t('dispo.modal.subtitle')}
      gradientFrom="from-red-500"
      gradientTo="to-orange-500"
      icon={<AlertCircle className="h-10 w-10 text-white" />}
      featureName="Dispo Prevention"
    >
      {/* What Is Section */}
      <section className="mb-16">
        <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-3xl p-8 lg:p-12 border border-red-500/20">
          <div className="flex items-start gap-6">
            <div className="p-4 bg-red-500/20 rounded-2xl">
              <Sparkles className="h-8 w-8 text-red-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">{t('dispo.modal.whatIs')}</h2>
              <p className="text-gray-300 text-lg leading-relaxed">{t('dispo.modal.intro')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
          <Target className="h-8 w-8 text-red-400" />
          {t('dispo.modal.howItWorks')}
        </h2>

        <div className="grid lg:grid-cols-2 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`bg-white/5 rounded-2xl p-8 border border-${step.color}-500/20 hover:border-${step.color}-500/40 transition-colors`}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 bg-${step.color}-500/20 rounded-xl`}>
                  <step.icon className={`h-7 w-7 text-${step.color}-400`} />
                </div>
                <h3 className="text-xl font-bold text-white">{step.title}</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Dispo is Costly */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8">{t('dispo.modal.whyCostly')}</h2>

        <div className="grid md:grid-cols-3 gap-6">
          {costReasons.map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-red-500/20"
            >
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <reason.icon className="h-7 w-7 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
              <p className="text-gray-300">{reason.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How AI Helps */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8">{t('dispo.modal.aiHelps')}</h2>

        <div className="space-y-6">
          {aiFeatures.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 border border-blue-500/30 flex items-start gap-6"
            >
              <div className="p-3 bg-blue-500/30 rounded-xl">
                <feature.icon className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section>
        <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-3xl p-8 lg:p-12 border border-red-500/30 text-center">
          <CheckCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">{t('dispo.modal.cta')}</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            {t('dispo.modal.ctaDescription')}
          </p>
          <a
            href="/#waitlist"
            className="inline-block px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all transform hover:scale-105"
          >
            Join Waitlist Now
          </a>
        </div>
      </section>
    </FeatureLayout>
  )
}
